import { useEffect } from "react";
import { Routes, Route, Link } from "react-router";
import { Toaster } from "sonner";
import LoginPage from "./pages/Auth/LoginPage";
import CallbackPage from "./pages/Auth/CallbackPage";
import SignupPage from "./pages/Auth/SignupPage";
import VerifyPage from "./pages/Auth/VerifyPage";
import ForgotPasswordPage from "./pages/Auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/Auth/ResetPasswordPage";
import NfcScanPage from "./pages/NfcScan/NfcScanPage";
import PairingHelpPage from "./pages/PairingHelp/PairingHelpPage";
import ContactPage from "./pages/Contact/ContactPage";
import SupportPage from "./pages/Support/SupportPage";
import NotFoundPage from "./pages/Error/NotFoundPage";
import { Button, ProtectedRoute } from "./ui/components";
import ProfilePage from "./pages/Profile/ProfilePage";
import { PublicTrophyRoomPage } from "./pages/PublicTrophyRoom";
import { TrophyRoomPage } from "./pages/TrophyRoomPage";
import { HomePage } from "./pages/Home/HomePage";
import { WelcomePage } from "./pages/Welcome/WelcomePage";
import { useEmbeddedWalletSync } from "@/hooks/useEmbeddedWalletSync";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import { validateSession, selectIsAuthenticated } from "./store/slices/authSlice";

function App() {
  // Auto-sync embedded wallets after login (runs in background)
  useEmbeddedWalletSync();

  // Session validation on mount (Story 4.9)
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  useEffect(() => {
    // Only validate if user appears to be authenticated (token in Redux persist)
    if (isAuthenticated) {
      dispatch(validateSession());
    }
  }, []); // Empty deps - only run on mount

  return (
    <>
      {/* User info display in top-right corner */}
      {/* <div className="fixed top-4 right-4 z-50">
        <UserInfo />
      </div> */}

      <Routes>
        {/* Onboarding routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/welcome" element={<WelcomePage />} />

        {/* Auth routes */}
        <Route path="/auth/login" element={<LoginPage />} />
        <Route path="/auth/signup" element={<SignupPage />} />
        <Route path="/auth/verify" element={<VerifyPage />} />
        <Route path="/auth/callback" element={<CallbackPage />} />
        <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />

        {/* Owner routes (authenticated) */}
        <Route path="/owner/profile" element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        } />
        <Route path="/owner/trophy-room" element={
          <ProtectedRoute>
            <TrophyRoomPage />
          </ProtectedRoute>
        } />

        {/* Public routes */}
        <Route path="/trophy-room/:userId" element={<PublicTrophyRoomPage />} />
        <Route
          path="/nfc/:productId/:itemId/:signature"
          element={<NfcScanPage />}
        />
        <Route path="/pairing-help" element={<PairingHelpPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/support" element={<SupportPage />} />

        {/* Development/testing route */}
        <Route path="/test" element={<TestPage />} />

        {/* 404 fallback */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <Toaster
        position="top-center"
        richColors
        className="z-[9999]"
        toastOptions={{
          style: {
            marginTop: '4.5rem',  // 60px header + 12px gap
          },
        }}
      />
    </>
  );
}

/**
 * TestPage - Development/Testing Navigation Page
 *
 * This page provides quick navigation links to various pages in the app
 * for development and testing purposes. It was the original "/" route
 * before the onboarding flow was implemented.
 *
 * Route: /test
 */
function TestPage() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center space-y-6">
        <div>
          <h1 className="text-2xl font-bold font-montserrat text-gray-900">
            ANYW3AR Webapp - Test Page
          </h1>
          <p className="text-gray-500 font-montserrat mt-2">
            Development navigation links
          </p>
        </div>

        <div className="flex flex-col gap-4 items-center">
          <Link to="/">
            <Button variant="primary-red" size="primary-red">
              Home (Onboarding)
            </Button>
          </Link>
          <Link to="/welcome">
            <Button variant="primary-red" size="primary-red">
              Welcome Page (First Login)
            </Button>
          </Link>
          <Link to="/auth/login">
            <Button variant="primary-red" size="primary-red">
              Go to Login Page
            </Button>
          </Link>
          <Link to="/owner/profile">
            <Button variant="primary-red" size="primary-red">
              Profile page
            </Button>
          </Link>
          <Link to="/owner/trophy-room">
            <Button variant="primary-red" size="primary-red">
              My Trophy Room (Owner)
            </Button>
          </Link>
          <Link to="/trophy-room/820c32e9-2e6b-4ddb-b050-2829d6b5b013">
            trophy room viewer (public)
          </Link>
          <Link to="/nfc/8297830973664/1/VVeW46BMZbKXO-9evOEZCoPbS3J1PvHadjLunSqKRFMfjq5i0ZeRhs78nR7sLhPPd2xJ3wIOSKHP-B8k-ScLBQ">
            <Button variant="primary-red" size="primary-red">
              Pairing item - reported stolen
            </Button>
          </Link>
          <Link to="/nfc/8297830973664/5/gXBnsOh4U5ifIwtIT-YdWAHHqrPol7Kp2QfFbF5CldjAtQ81AA9MZGBCEe2c0veTZoaP9p-XKW1APNwn7Xl7Bw">
            <Button variant="primary-red" size="primary-red">
              Pairing item - ability cotton
            </Button>
          </Link>

          <Link to="/nfc/8297830875360/1/YNGJlxVpW0jVUTOzWdt2MkI85JeaLUzrgaRlMmI-piyAXh9HjsuC1DmDfY5jmBp414dKzG3mmlVW5iG85UtUDg">
            <Button variant="primary-red" size="primary-red">
              Pairing partner - code toto hero (custom)
            </Button>
          </Link>
          <Link to="/nfc/8645164204256/1/f3jjFQn-ajG6Uww5nxo0YvL2v9z9OrPJX9aU8PZtOBJ2ipPVtWGDKXfBW9qVqbMCzyQR7aviBaaDwMzPjn_rAQ">
            <Button variant="primary-red" size="primary-red">
              Pairing miguel
            </Button>
          </Link>
          <Link to="/nfc/8645164204256/50/f3jjFQn-ajG6Uww5nxo0YvL2v9z9OrPJX9aU8PZtOBJ2ipPVtWGDKXfBW9qVqbMCzyQR7aviBaaDwMzPjn_rAQ">
            <Button variant="primary-red" size="primary-red">
              invalid signature
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default App;
